import { useEffect, useState } from "react";
import { invoiceService } from "../../services/invoiceService";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import AsyncCustomerSelect from "../../components/Common/AsyncCustomerSelect";
import { useToast } from "../../context/ToastContext";
import { useI18n } from "../../i18n/LanguageContext";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—";
const fmtMoney = (v) =>
  v != null ? `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺` : "—";

const emptyItem = { itemName: "", itemType: "SERVICE", quantity: 1, unit: "ADET", unitPrice: 0, vatRate: 20, discountAmount: 0 };

export default function InvoicesPage() {
  const { showToast } = useToast();
  const { t, lang } = useI18n();
  const pdfLocale = lang === "en" ? "en-GB" : "tr-TR";
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Arama & Filtreleme
  const [search, setSearch] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Dönem filtresi — varsayılan olarak gün içi (bugün) faturalar listelenir.
  const [dateRange, setDateRange] = useState("today");

  // Modal yönetimi
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });

  // Yeni Fatura Formu
  const [customerId, setCustomerId] = useState("");
  const [customerTitle, setCustomerTitle] = useState("");
  const [customerTaxOffice, setCustomerTaxOffice] = useState("");
  const [customerTaxNo, setCustomerTaxNo] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [documentType, setDocumentType] = useState("INVOICE");
  const [dueDate, setDueDate] = useState("");
  const [items, setItems] = useState([ { ...emptyItem } ]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await invoiceService.list();
      setInvoices(res.data?.data ?? []);
    } catch (err) {
      showToast("Faturalar yüklenirken hata oluştu.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setCustomerId("");
    setCustomerTitle("");
    setCustomerTaxOffice("");
    setCustomerTaxNo("");
    setCustomerAddress("");
    setDocumentType("INVOICE");
    setDueDate(new Date(Date.now() + 7 * 86400000).toISOString().split("T")[0]); // 7 gün sonrası
    setItems([ { ...emptyItem } ]);
    setCreateModalOpen(true);
  };

  const handleCustomerChange = (id, customer) => {
    setCustomerId(id);
    if (customer) {
      setCustomerTitle(customer.fullName || "");
      setCustomerAddress(customer.address || "");
    } else {
      setCustomerTitle("");
      setCustomerAddress("");
    }
  };

  const handleAddItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, val) => {
    const nextItems = [...items];
    nextItems[index][field] = val;
    setItems(nextItems);
  };

  // Fatura Toplam Hesaplamaları (Frontend Önizleme)
  const calculateTotals = () => {
    let subtotal = 0;
    let vatTotal = 0;
    let discountTotal = 0;
    let grandTotal = 0;

    items.forEach((item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      const vatR = Number(item.vatRate) || 0;
      const disc = Number(item.discountAmount) || 0;

      const lineSub = price * qty;
      const lineVat = lineSub * (vatR / 100);
      const lineTotal = lineSub + lineVat - disc;

      subtotal += lineSub;
      vatTotal += lineVat;
      discountTotal += disc;
      grandTotal += lineTotal;
    });

    return { subtotal, vatTotal, discountTotal, grandTotal };
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (items.some(item => !item.itemName.trim())) {
      showToast("Lütfen tüm kalemlerin adını giriniz.", "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customerId: customerId || null,
        documentType,
        customerTitle: customerTitle.trim() || "Perakende Müşteri",
        customerTaxOffice: customerTaxOffice.trim() || null,
        customerTaxNo: customerTaxNo.trim() || null,
        customerAddress: customerAddress.trim() || null,
        dueDate: dueDate || null,
        referenceType: "MANUAL",
        referenceId: null,
        items: items.map(i => ({
          itemName: i.itemName,
          itemType: i.itemType,
          quantity: Number(i.quantity),
          unit: i.unit,
          unitPrice: Number(i.unitPrice),
          vatRate: Number(i.vatRate),
          discountAmount: Number(i.discountAmount),
          referenceItemId: null
        }))
      };
      await invoiceService.create(payload);
      showToast("Fatura/Makbuz başarıyla oluşturuldu.", "success");
      setCreateModalOpen(false);
      fetchInvoices();
    } catch (err) {
      showToast("Fatura oluşturulurken hata oluştu.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleViewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setViewModalOpen(true);
    setDetailsLoading(true);
    try {
      const res = await invoiceService.get(invoice.id);
      setSelectedInvoiceDetails(res.data?.data);
    } catch (err) {
      showToast("Fatura detayları yüklenemedi.", "error");
      setViewModalOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCancelInvoice = (invoice) => {
    setConfirmModal({
      isOpen: true,
      title: "Fatura İptal Onayı",
      message: `${invoice.invoiceNo} numaralı faturayı iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
      onConfirm: async () => {
        try {
          await invoiceService.cancel(invoice.id);
          showToast("Fatura iptal edildi.", "success");
          setConfirmModal({ isOpen: false });
          if (selectedInvoice && selectedInvoice.id === invoice.id) {
            setViewModalOpen(false);
          }
          fetchInvoices();
        } catch (err) {
          showToast("Fatura iptal edilirken hata oluştu.", "error");
        }
      }
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Dönem filtresi: seçilen aralığa göre issueDate kontrolü.
  const inDateRange = (iso) => {
    if (dateRange === "all") return true;
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (dateRange === "today") {
      const end = new Date(startOfToday);
      end.setDate(end.getDate() + 1);
      return d >= startOfToday && d < end;
    }
    if (dateRange === "week") {
      const dow = (startOfToday.getDay() + 6) % 7; // 0 = Pazartesi
      const start = new Date(startOfToday);
      start.setDate(start.getDate() - dow);
      const end = new Date(start);
      end.setDate(start.getDate() + 7);
      return d >= start && d < end;
    }
    if (dateRange === "month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return d >= start && d < end;
    }
    return true;
  };

  // Arama filtreleme mantığı
  const filteredInvoices = invoices.filter((inv) => {
    const matchesSearch =
      inv.invoiceNo.toLowerCase().includes(search.toLowerCase()) ||
      inv.customerTitle.toLowerCase().includes(search.toLowerCase());
    const matchesDocType = !docTypeFilter || inv.documentType === docTypeFilter;
    const matchesStatus = !statusFilter || inv.status === statusFilter;
    const matchesDate = inDateRange(inv.issueDate);
    return matchesSearch && matchesDocType && matchesStatus && matchesDate;
  });

  const columns = [
    {
      key: "invoiceNo",
      header: "Belge No",
      render: (v, row) => (
        <span className="font-semibold text-primary">{row.invoiceNo}</span>
      )
    },
    {
      key: "documentType",
      header: "Belge Türü",
      render: (v, row) => (
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
          row.documentType === "INVOICE" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
        }`}>
          <Icon name={row.documentType === "INVOICE" ? "description" : "receipt"} className="!text-[14px]" />
          {row.documentType === "INVOICE" ? "Fatura" : "Makbuz"}
        </span>
      )
    },
    {
      key: "customerTitle",
      header: "Müşteri / Ünvan",
      render: (v, row) => <span className="font-medium">{row.customerTitle}</span>
    },
    {
      key: "issueDate",
      header: "Tarih",
      render: (v, row) => fmtDateTime(row.issueDate)
    },
    {
      key: "grandTotal",
      header: "Toplam Tutar",
      render: (v, row) => <span className="font-bold text-on-surface">{fmtMoney(row.grandTotal)}</span>
    },
    {
      key: "status",
      header: "Durum",
      render: (v, row) => (
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
          row.status === "ISSUED" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}>
          <Icon name={row.status === "ISSUED" ? "check_circle" : "cancel"} className="!text-[14px]" />
          {row.status === "ISSUED" ? "Kesildi" : "İptal Edildi"}
        </span>
      )
    },
    {
      key: "actions",
      header: "Aksiyonlar",
      render: (v, row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleViewInvoice(row)}
            className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
            title="Görüntüle / Yazdır"
          >
            <Icon name="visibility" className="!text-[18px]" />
          </button>
          {row.status === "ISSUED" && (
            <button
              onClick={() => handleCancelInvoice(row)}
              className="p-1.5 rounded-lg text-error hover:bg-error/10 transition"
              title="İptal Et"
            >
              <Icon name="cancel" className="!text-[18px]" />
            </button>
          )}
        </div>
      )
    }
  ];

  const totals = calculateTotals();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-on-surface flex items-center gap-2">
            <Icon name="receipt_long" className="text-primary" /> Faturalar ve Makbuzlar
          </h2>
          <p className="text-sm text-on-surface-variant">
            Salonunuzun tüm fatura ve makbuz kayıtlarını izleyin, manuel fatura oluşturun ve yazdırın.
          </p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-semibold py-2.5 px-4 rounded-xl shadow-lg transition"
        >
          <Icon name="add" /> Yeni Belge Ekle
        </button>
      </div>

      {/* Filtreler */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 bg-surface-card p-4 rounded-2xl border border-outline-variant">
        <div className="relative">
          <input
            type="text"
            placeholder="Belge No veya Ünvan Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
          />
          <Icon name="search" className="absolute left-2.5 top-2.5 text-on-surface-variant !text-[18px]" />
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
        >
          <option value="today">Bugün</option>
          <option value="week">Bu Hafta</option>
          <option value="month">Bu Ay</option>
          <option value="all">Tüm Zamanlar</option>
        </select>
        <select
          value={docTypeFilter}
          onChange={(e) => setDocTypeFilter(e.target.value)}
          className="px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
        >
          <option value="">Tüm Belge Türleri</option>
          <option value="INVOICE">Fatura</option>
          <option value="RECEIPT">Makbuz</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
        >
          <option value="">Tüm Durumlar</option>
          <option value="ISSUED">Kesildi</option>
          <option value="CANCELLED">İptal Edildi</option>
        </select>
        <button
          onClick={() => { setSearch(""); setDocTypeFilter(""); setStatusFilter(""); setDateRange("today"); }}
          className="flex items-center justify-center gap-1 text-sm font-semibold text-primary hover:bg-primary/5 py-2 rounded-xl transition"
        >
          <Icon name="filter_alt_off" className="!text-[18px]" /> Filtreleri Temizle
        </button>
      </div>

      {/* Tablo */}
      <div className="bg-surface-card rounded-2xl border border-outline-variant overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-on-surface-variant">Yükleniyor...</div>
        ) : (
          <AdvancedTable columns={columns} data={filteredInvoices} />
        )}
      </div>

      {/* MODAL: Yeni Fatura/Makbuz Ekle */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-surface-card border border-outline-variant rounded-3xl w-full max-w-4xl shadow-modal my-8">
            <div className="border-b border-outline-variant p-6 flex items-center justify-between">
              <h3 className="text-lg font-bold text-on-surface flex items-center gap-2">
                <Icon name="receipt_long" className="text-primary" /> Yeni Belge Düzenle
              </h3>
              <button onClick={() => setCreateModalOpen(false)} className="text-on-surface-variant hover:text-on-surface">
                <Icon name="close" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-on-surface-variant mb-1.5">Belge Türü</label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="w-full px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
                  >
                    <option value="INVOICE">Fatura</option>
                    <option value="RECEIPT">Makbuz</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-on-surface-variant mb-1.5">Vade Tarihi</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-on-surface-variant mb-1.5">Müşteri Seçimi (Kayıtlı)</label>
                  <AsyncCustomerSelect
                    value={customerId}
                    onChange={handleCustomerChange}
                    placeholder="Müşteri ara..."
                  />
                </div>
              </div>

              {/* Fatura/Makbuz Alıcı Bilgileri */}
              <div className="bg-surface-container-low p-4 rounded-2xl border border-outline-variant space-y-4">
                <h4 className="text-sm font-bold text-on-surface flex items-center gap-1.5">
                  <Icon name="business" className="text-primary !text-[18px]" /> Fatura/Makbuz Alıcı Bilgileri
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-on-surface-variant mb-1.5">Ad Soyad / Ticari Ünvan</label>
                    <input
                      type="text"
                      required
                      placeholder="Müşteri Adı veya Firma Ünvanı"
                      value={customerTitle}
                      onChange={(e) => setCustomerTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-on-surface-variant mb-1.5">Vergi No / T.C. Kimlik</label>
                    <input
                      type="text"
                      maxLength={11}
                      placeholder="T.C. veya Vergi No"
                      value={customerTaxNo}
                      onChange={(e) => setCustomerTaxNo(e.target.value)}
                      className="w-full px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-on-surface-variant mb-1.5">Vergi Dairesi</label>
                    <input
                      type="text"
                      placeholder="Vergi Dairesi"
                      value={customerTaxOffice}
                      onChange={(e) => setCustomerTaxOffice(e.target.value)}
                      className="w-full px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-on-surface-variant mb-1.5">Adres</label>
                    <input
                      type="text"
                      placeholder="Fatura Adresi"
                      value={customerAddress}
                      onChange={(e) => setCustomerAddress(e.target.value)}
                      className="w-full px-3 py-2 border border-outline-variant rounded-xl outline-none focus:border-primary text-sm bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Kalemler Tablosu */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold text-on-surface flex items-center gap-1.5">
                    <Icon name="list" className="text-primary !text-[18px]" /> Fatura Kalemleri
                  </h4>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="flex items-center gap-1 text-xs font-semibold text-primary hover:bg-primary/5 px-2.5 py-1.5 rounded-xl border border-primary/20 transition"
                  >
                    <Icon name="add" className="!text-[14px]" /> Satır Ekle
                  </button>
                </div>

                <div className="overflow-x-auto border border-outline-variant rounded-2xl">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="bg-surface-container-low text-on-surface-variant border-b border-outline-variant">
                        <th className="p-3 text-xs font-bold">Hizmet/Ürün Adı</th>
                        <th className="p-3 text-xs font-bold w-32">Tür</th>
                        <th className="p-3 text-xs font-bold w-24">Miktar</th>
                        <th className="p-3 text-xs font-bold w-32">Birim Fiyat (KDV Hariç)</th>
                        <th className="p-3 text-xs font-bold w-24">KDV (%)</th>
                        <th className="p-3 text-xs font-bold w-24">İndirim</th>
                        <th className="p-3 text-xs font-bold w-12 text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={idx} className="border-b border-outline-variant/50 last:border-none">
                          <td className="p-2">
                            <input
                              type="text"
                              required
                              placeholder="Hizmet / Ürün Adı"
                              value={item.itemName}
                              onChange={(e) => handleItemChange(idx, "itemName", e.target.value)}
                              className="w-full px-2 py-1.5 border border-outline-variant rounded-lg outline-none focus:border-primary text-xs"
                            />
                          </td>
                          <td className="p-2">
                            <select
                              value={item.itemType}
                              onChange={(e) => handleItemChange(idx, "itemType", e.target.value)}
                              className="w-full px-2 py-1.5 border border-outline-variant rounded-lg outline-none focus:border-primary text-xs bg-white"
                            >
                              <option value="SERVICE">Hizmet</option>
                              <option value="PRODUCT">Ürün</option>
                            </select>
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              min={1}
                              step="any"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(idx, "quantity", e.target.value)}
                              className="w-full px-2 py-1.5 border border-outline-variant rounded-lg outline-none focus:border-primary text-xs"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              min={0}
                              step="any"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(idx, "unitPrice", e.target.value)}
                              className="w-full px-2 py-1.5 border border-outline-variant rounded-lg outline-none focus:border-primary text-xs"
                            />
                          </td>
                          <td className="p-2">
                            <select
                              value={item.vatRate}
                              onChange={(e) => handleItemChange(idx, "vatRate", e.target.value)}
                              className="w-full px-2 py-1.5 border border-outline-variant rounded-lg outline-none focus:border-primary text-xs bg-white"
                            >
                              <option value={20}>20%</option>
                              <option value={10}>10%</option>
                              <option value={1}>1%</option>
                              <option value={0}>0%</option>
                            </select>
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              min={0}
                              step="any"
                              value={item.discountAmount}
                              onChange={(e) => handleItemChange(idx, "discountAmount", e.target.value)}
                              className="w-full px-2 py-1.5 border border-outline-variant rounded-lg outline-none focus:border-primary text-xs"
                            />
                          </td>
                          <td className="p-2 text-center">
                            <button
                              type="button"
                              disabled={items.length === 1}
                              onClick={() => handleRemoveItem(idx)}
                              className="text-error hover:text-error-dark disabled:opacity-30 transition"
                            >
                              <Icon name="delete" className="!text-[18px]" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Toplam Tutar Alanı */}
              <div className="flex justify-end">
                <div className="w-80 bg-surface-container-low border border-outline-variant p-4 rounded-2xl space-y-2 text-sm">
                  <div className="flex justify-between text-on-surface-variant font-medium">
                    <span>Ara Toplam (KDV Hariç):</span>
                    <span>{fmtMoney(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-on-surface-variant font-medium">
                    <span>Hesaplanan KDV Toplamı:</span>
                    <span>{fmtMoney(totals.vatTotal)}</span>
                  </div>
                  {totals.discountTotal > 0 && (
                    <div className="flex justify-between text-error font-medium">
                      <span>Toplam İndirim:</span>
                      <span>-{fmtMoney(totals.discountTotal)}</span>
                    </div>
                  )}
                  <div className="border-t border-outline-variant/50 pt-2 flex justify-between font-bold text-on-surface text-base">
                    <span>Genel Toplam:</span>
                    <span>{fmtMoney(totals.grandTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Form Footer */}
              <div className="border-t border-outline-variant pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setCreateModalOpen(false)}
                  className="px-4 py-2 border border-outline-variant text-on-surface font-semibold rounded-xl hover:bg-surface-container-high transition"
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl shadow-md transition disabled:opacity-50"
                >
                  {saving ? "Kaydediliyor..." : "Belgeyi Oluştur"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Fatura Görüntüleme & A4 Yazdırma Şablonu */}
      {viewModalOpen && selectedInvoice && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto print:p-0 print:bg-white print:relative">
          <div
          style={{ backgroundColor: "#ffffff" }}
          className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl border border-outline-variant overflow-hidden print:shadow-none print:border-none print:rounded-none"
        >
            {/* Modal Header (Hides on Print) */}
            <div className="bg-surface-container-low border-b border-outline-variant p-5 flex items-center justify-between print:hidden">
              <h3 className="text-base font-bold text-on-surface flex items-center gap-1.5">
                <Icon name="visibility" className="text-primary" /> {selectedInvoice.invoiceNo} Belge Detayı
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white text-xs font-bold px-3.5 py-2 rounded-xl transition shadow"
                >
                  <Icon name="print" className="!text-[16px]" /> Yazdır
                </button>
                {selectedInvoiceDetails?.status === "ISSUED" && (
                  <button
                    onClick={() => handleCancelInvoice(selectedInvoiceDetails)}
                    className="flex items-center gap-1.5 bg-error/10 hover:bg-error/20 text-error text-xs font-bold px-3.5 py-2 rounded-xl transition"
                  >
                    <Icon name="cancel" className="!text-[16px]" /> İptal Et
                  </button>
                )}
                <button
                  onClick={() => setViewModalOpen(false)}
                  className="text-on-surface-variant hover:text-on-surface p-1"
                >
                  <Icon name="close" />
                </button>
              </div>
            </div>

            {/* A4 PRINT CONTAINER */}
            <div id="print-area" style={{ backgroundColor: "#ffffff" }} className="p-8 md:p-12 text-gray-800 bg-white font-sans text-xs leading-normal print:p-0">
              {detailsLoading ? (
                <div className="text-center py-12 text-gray-500 font-medium">{t("inv.pdf.loading")}</div>
              ) : selectedInvoiceDetails ? (
                <div className="space-y-8">
                  {/* Top Header Section */}
                  <div className="flex justify-between items-start border-b-2 border-gray-200 pb-6">
                    <div>
                      {/* Logo placeholder or Salon Name */}
                      <h1 className="text-lg font-bold text-primary flex items-center gap-1 mb-2">
                        <Icon name="spa" filled className="text-primary !text-[24px]" /> {selectedInvoiceDetails.salonTitle}
                      </h1>
                      <div className="space-y-1 text-gray-500">
                        <p>{selectedInvoiceDetails.salonAddress}</p>
                        <p className="font-semibold">{t("inv.pdf.taxOfficeShort")}: <span className="font-normal">{selectedInvoiceDetails.salonTaxOffice || t("inv.pdf.taxOfficeDefault")}</span></p>
                        <p className="font-semibold">{t("inv.pdf.taxNoLabel")}: <span className="font-normal">{selectedInvoiceDetails.salonTaxNo}</span></p>
                      </div>
                    </div>
                    <div className="text-right space-y-1.5">
                      <h2 className="text-lg font-black text-gray-900 tracking-wider">
                        {selectedInvoiceDetails.documentType === "INVOICE" ? t("inv.pdf.titleInvoice") : t("inv.pdf.titleReceipt")}
                      </h2>
                      <div className="inline-block border border-gray-300 rounded-lg p-2.5 bg-gray-50 space-y-1 text-[11px]">
                        <p className="font-bold">{t("inv.pdf.docNo")}: <span className="font-normal text-primary">{selectedInvoiceDetails.invoiceNo}</span></p>
                        <p className="font-bold">{t("inv.pdf.date")}: <span className="font-normal">{new Date(selectedInvoiceDetails.issueDate).toLocaleDateString(pdfLocale)}</span></p>
                        {selectedInvoiceDetails.documentType === "INVOICE" && (
                          <p className="font-bold">{t("inv.pdf.dueDate")}: <span className="font-normal">{selectedInvoiceDetails.dueDate ? new Date(selectedInvoiceDetails.dueDate).toLocaleDateString(pdfLocale) : "—"}</span></p>
                        )}
                        <p className="font-bold">{t("inv.pdf.status")}: <span className={`font-semibold ${selectedInvoiceDetails.status === "ISSUED" ? "text-green-600" : "text-red-600"}`}>
                          {selectedInvoiceDetails.status === "ISSUED" ? t("inv.pdf.statusIssued") : t("inv.pdf.statusCancelled")}
                        </span></p>
                      </div>
                    </div>
                  </div>

                  {/* Customer Information Block */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">{t("inv.pdf.customerInfo")}</p>
                      <h3 className="font-bold text-gray-900 text-sm mb-1">{selectedInvoiceDetails.customerTitle}</h3>
                      <p className="text-gray-500 whitespace-pre-line">{selectedInvoiceDetails.customerAddress || t("inv.pdf.retail")}</p>
                    </div>
                    <div className="md:text-right space-y-1 text-gray-600">
                      {selectedInvoiceDetails.customerTaxNo && (
                        <p className="font-bold">{t("inv.pdf.customerTaxNo")}: <span className="font-normal">{selectedInvoiceDetails.customerTaxNo}</span></p>
                      )}
                      {selectedInvoiceDetails.customerTaxOffice && (
                        <p className="font-bold">{t("inv.pdf.customerTaxOffice")}: <span className="font-normal">{selectedInvoiceDetails.customerTaxOffice}</span></p>
                      )}
                      {selectedInvoiceDetails.referenceType && selectedInvoiceDetails.referenceType !== "MANUAL" && (
                        <p className="font-bold">{t("inv.pdf.sourceTx")}: <span className="font-normal text-gray-500">{selectedInvoiceDetails.referenceType} ({selectedInvoiceDetails.referenceId?.substring(0, 8)})</span></p>
                      )}
                    </div>
                  </div>

                  {/* Table items */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-100 text-gray-700 font-bold border-b border-gray-200">
                          <th className="p-3 w-10 text-center">#</th>
                          <th className="p-3">{t("inv.pdf.colDesc")}</th>
                          <th className="p-3 w-20 text-center">{t("inv.pdf.colType")}</th>
                          <th className="p-3 w-16 text-center">{t("inv.pdf.colQty")}</th>
                          <th className="p-3 w-24 text-right">{t("inv.pdf.colUnitPrice")}</th>
                          <th className="p-3 w-16 text-center">{t("inv.pdf.colVat")}</th>
                          <th className="p-3 w-24 text-right">{t("inv.pdf.colAmount")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoiceDetails.items?.map((item, idx) => (
                          <tr key={item.id} className="border-b border-gray-100 last:border-none hover:bg-gray-50/50">
                            <td className="p-3 text-center text-gray-400 font-medium">{idx + 1}</td>
                            <td className="p-3 font-semibold text-gray-900">{item.itemName}</td>
                            <td className="p-3 text-center text-gray-500">{item.itemType === "SERVICE" ? t("inv.pdf.typeService") : t("inv.pdf.typeProduct")}</td>
                            <td className="p-3 text-center">{item.quantity} {item.unit}</td>
                            <td className="p-3 text-right">{fmtMoney(item.unitPrice)}</td>
                            <td className="p-3 text-center font-medium text-gray-600">%{item.vatRate}</td>
                            <td className="p-3 text-right font-semibold text-gray-900">{fmtMoney(item.totalPrice)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Financial Summary Block */}
                  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pt-4">
                    {/* Left: Stamp/Signature & Notes */}
                    <div className="flex-1 space-y-4">
                      <div className="text-[10px] text-gray-400 font-medium">
                        <p>{t("inv.pdf.legalNote")}</p>
                      </div>
                      <div className="border border-dashed border-gray-200 rounded-xl p-3 inline-block w-48 text-center bg-gray-50">
                        <span className="text-[9px] font-bold text-gray-400 block mb-8 uppercase tracking-wider">{t("inv.pdf.stamp")}</span>
                        <div className="h-6"></div>
                      </div>
                    </div>
                    {/* Right: Calculations */}
                    <div className="w-72 border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-2.5 text-xs">
                      <div className="flex justify-between text-gray-500 font-medium">
                        <span>{t("inv.pdf.subtotal")}:</span>
                        <span>{fmtMoney(selectedInvoiceDetails.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500 font-medium">
                        <span>{t("inv.pdf.vatTotal")}:</span>
                        <span>{fmtMoney(selectedInvoiceDetails.vatTotal)}</span>
                      </div>
                      {Number(selectedInvoiceDetails.discountTotal) > 0 && (
                        <div className="flex justify-between text-red-600 font-medium">
                          <span>{t("inv.pdf.discountTotal")}:</span>
                          <span>-{fmtMoney(selectedInvoiceDetails.discountTotal)}</span>
                        </div>
                      )}
                      <div className="border-t border-gray-200 pt-2 flex justify-between font-black text-gray-900 text-sm">
                        <span>{t("inv.pdf.grandTotal")}:</span>
                        <span>{fmtMoney(selectedInvoiceDetails.grandTotal)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-error">{t("inv.pdf.loadError")}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: İptal Onay Modalı */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false })}
      />
    </div>
  );
}
